import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import useAuthStore from "../../Zustand/Store/useAuthStore";
import { MAIN_URL } from "../../Configurations/Urls";
import "./SlotAvailability.css";

const DEFAULT_TIME_SLOTS = Array.from({ length: 16 }, (_, index) => {
  const hour = 6 + index;
  return { key: `${String(hour).padStart(2, "0")}00` };
});

const timeToMinutes = (value) => {
  if (!value) return null;
  const raw = String(value).trim();
  const ampmMatch = raw.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (ampmMatch) {
    let hour = Number(ampmMatch[1]);
    const minute = Number(ampmMatch[2]);
    const ampm = ampmMatch[3].toUpperCase();
    if (ampm === "PM" && hour !== 12) hour += 12;
    if (ampm === "AM" && hour === 12) hour = 0;
    return hour * 60 + minute;
  }
  const hhmmMatch = raw.match(/^(\d{1,2}):(\d{2})$/);
  if (hhmmMatch) return Number(hhmmMatch[1]) * 60 + Number(hhmmMatch[2]);
  const hhmmssMatch = raw.match(/^(\d{1,2}):(\d{2}):(\d{2})$/);
  if (hhmmssMatch) return Number(hhmmssMatch[1]) * 60 + Number(hhmmssMatch[2]);
  return null;
};

const formatMinutesToTime = (totalMinutes) => {
  const minutes = Math.max(0, Math.min(23 * 60 + 59, Number(totalMinutes) || 0));
  const hour24 = Math.floor(minutes / 60);
  const minute = minutes % 60;
  const ampm = hour24 < 12 ? "AM" : "PM";
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return `${hour12}:${String(minute).padStart(2, "0")} ${ampm}`;
};

const getHalfHourLabels = (hourKey) => {
  const hour24 = Number(String(hourKey || "").slice(0, 2));
  if (!Number.isFinite(hour24)) return { top: "-", bottom: "-" };
  const start = hour24 * 60;
  const mid = start + 30;
  const end = start + 60;
  return {
    top: `${formatMinutesToTime(start)} - ${formatMinutesToTime(mid)}`,
    bottom: `${formatMinutesToTime(mid)} - ${formatMinutesToTime(end)}`,
  };
};

const formatShiftRange = (startTime, endTime, fallbackName = "-") => {
  const start = timeToMinutes(startTime);
  const endRaw = timeToMinutes(endTime);
  if (!Number.isFinite(start) || !Number.isFinite(endRaw)) return fallbackName || "-";
  const end = endRaw <= start ? endRaw + 24 * 60 : endRaw;
  return `${formatMinutesToTime(start)} - ${formatMinutesToTime(end % (24 * 60))}`;
};

const hasOverlap = (startA, endA, startB, endB) =>
  Number.isFinite(startA) &&
  Number.isFinite(endA) &&
  Number.isFinite(startB) &&
  Number.isFinite(endB) &&
  startA < endB &&
  endA > startB;

const TrainerSlotAvailability = () => {
  const { userData } = useAuthStore();
  const token = localStorage.getItem("token");
  const organizationId =
    userData?.organization?.organization_id ||
    userData?.activeOrganizationEntity?.organization_id ||
    userData?.organization_id;

  const [selectedDate, setSelectedDate] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  const fetchData = async () => {
    if (!organizationId) return;
    setLoading(true);
    try {
      const payload = {
        organization_id: organizationId,
        ...(selectedDate ? { date: selectedDate } : {}),
        ...(search.trim() ? { search: search.trim() } : {}),
      };

      const response = await axios.post(`${MAIN_URL}/api/ems/trainer-slots`, payload, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      setRows(Array.isArray(response?.data?.data) ? response.data.data : []);
    } catch (error) {
      console.error("Failed to fetch trainer slots", error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  const trainersForGrid = useMemo(() => {
    return (Array.isArray(rows) ? rows : []).map((trainer) => {
      const shiftStart = timeToMinutes(trainer?.workshift?.work_shift_start_time);
      const shiftEndRaw = timeToMinutes(trainer?.workshift?.work_shift_end_time);
      const shiftEnd =
        Number.isFinite(shiftStart) && Number.isFinite(shiftEndRaw) && shiftEndRaw <= shiftStart
          ? shiftEndRaw + 24 * 60
          : shiftEndRaw;

      const bookings = (Array.isArray(trainer?.batches) ? trainer.batches : [])
        .flatMap((batch) =>
          (Array.isArray(batch?.slots) ? batch.slots : []).map((slot) => {
            const start = timeToMinutes(slot?.start_time);
            const endRaw = timeToMinutes(slot?.end_time);
            const end =
              Number.isFinite(start) && Number.isFinite(endRaw) && endRaw <= start
                ? endRaw + 24 * 60
                : endRaw;
            return {
              ...slot,
              __startMinutes: start,
              __endMinutes: end,
              __batchId: batch?.batch_id || batch?.batch?.organization_ems_batch_id || null,
              __batchCode: batch?.batch?.batch_code || batch?.batch_code || "-",
              __batchName: batch?.batch?.batch_name || batch?.batch_name || "-",
              __course:
                batch?.batch?.training_program?.training_program_name ||
                batch?.training_program ||
                "-",
              __spaceName: slot?.training_space?.space_name || "-",
              __floor:
                slot?.training_space?.floor_number === null ||
                slot?.training_space?.floor_number === undefined
                  ? "-"
                  : String(slot.training_space.floor_number),
            };
          })
        )
        .filter((b) => Number.isFinite(b.__startMinutes) && Number.isFinite(b.__endMinutes))
        .filter((booking, index, arr) => {
          const bookingKey = [
            booking?.class_date || "",
            booking?.__batchId || booking?.__batchCode || "",
            booking?.start_time || "",
            booking?.end_time || "",
          ].join("|");
          return (
            arr.findIndex((item) => {
              const itemKey = [
                item?.class_date || "",
                item?.__batchId || item?.__batchCode || "",
                item?.start_time || "",
                item?.end_time || "",
              ].join("|");
              return itemKey === bookingKey;
            }) === index
          );
        })
        .sort((a, b) => a.__startMinutes - b.__startMinutes);

      return {
        id: String(trainer?.organization_person_id || Math.random()),
        name: trainer?.person?.full_name || "-",
        shiftName: formatShiftRange(
          trainer?.workshift?.work_shift_start_time,
          trainer?.workshift?.work_shift_end_time,
          trainer?.workshift?.work_shift_name || "-"
        ),
        shiftStart,
        shiftEnd,
        bookings,
      };
    });
  }, [rows]);

  const slotRows = useMemo(() => {
    return DEFAULT_TIME_SLOTS.map((slot, rowIndex) => {
      const rowHour = Number(slot.key.slice(0, 2));
      const rowStart = rowHour * 60;
      const rowMid = rowStart + 30;
      const rowEnd = rowStart + 60;

      const cellData = trainersForGrid.map((trainer) => {
        const bookings = trainer.bookings || [];
        const booking = bookings.find((b) => hasOverlap(b.__startMinutes, b.__endMinutes, rowStart, rowEnd)) || null;
        const prevRowStart = rowStart - 60;
        const prevRowEnd = rowStart;
        const alreadyShownInPreviousRow =
          booking && rowIndex > 0
            ? hasOverlap(booking.__startMinutes, booking.__endMinutes, prevRowStart, prevRowEnd)
            : false;
        const showBookingCard = Boolean(booking) && !alreadyShownInPreviousRow;
        const topShift = hasOverlap(trainer.shiftStart, trainer.shiftEnd, rowStart, rowMid);
        const bottomShift = hasOverlap(trainer.shiftStart, trainer.shiftEnd, rowMid, rowEnd);
        return { trainer, booking, showBookingCard, topShift, bottomShift };
      });

      return { slot, rowHour, rowStart, rowEnd, cellData };
    });
  }, [trainersForGrid]);

  const isEmpty = useMemo(() => {
    return !trainersForGrid.length || !trainersForGrid.some((t) => (t.bookings || []).length);
  }, [rows]);

  return (
    <section className="slot-availability">
      <header className="slot-availability__header">
        <h2 className="slot-availability__title">Trainer Slot Availability</h2>
      </header>

      <div className="slot-availability__filters" style={{ gridTemplateColumns: "220px 1fr 150px" }}>
        <label className="slot-select">
          <span>Date</span>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            style={{ height: 32, padding: "0 8px", border: "1px solid #d0d5dd", borderRadius: 8 }}
          />
        </label>
        <label className="slot-select">
          <span>Trainer</span>
          <input
            type="text"
            placeholder="Search trainer..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ height: 32, padding: "0 8px", border: "1px solid #d0d5dd", borderRadius: 8 }}
          />
        </label>
        <button type="button" onClick={fetchData} className="search-button" style={{ alignSelf: "end" }}>
          {loading ? "Loading..." : "Search"}
        </button>
      </div>

      <div className="slot-availability__table-wrap">
        <table className="slot-grid" style={{ minWidth: Math.max(900, (trainersForGrid.length + 1) * 140) }}>
          <thead>
            <tr>
              <th>Time</th>
              {trainersForGrid.map((trainer) => (
                <th key={trainer.id}>
                  <div className="slot-space-head">
                    <span className="slot-space-head__name">{trainer.name}</span>
                    <span className="slot-space-head__floor">{trainer.shiftName}</span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isEmpty ? (
              <tr>
                <td colSpan={Math.max(2, trainersForGrid.length + 1)} className="slot-time" style={{ padding: 12 }}>
                  No trainer slot data found.
                </td>
              </tr>
            ) : (
              slotRows.map(({ slot, rowHour, rowStart, cellData }) => (
                <tr key={slot.key}>
                  <td className="slot-time">
                    <div className="slot-time-split">
                      <span>{getHalfHourLabels(slot.key).top}</span>
                      <span>{getHalfHourLabels(slot.key).bottom}</span>
                    </div>
                  </td>
                  {cellData.map((cell) => {
                    const topBooked = cell.booking
                      ? hasOverlap(cell.booking.__startMinutes, cell.booking.__endMinutes, rowStart, rowStart + 30)
                      : false;
                    const bottomBooked = cell.booking
                      ? hasOverlap(cell.booking.__startMinutes, cell.booking.__endMinutes, rowStart + 30, rowStart + 60)
                      : false;
                    const bgTop = topBooked ? "booked" : cell.topShift ? "available" : "unavailable";
                    const bgBottom = bottomBooked
                      ? "booked"
                      : cell.bottomShift
                        ? "available"
                        : "unavailable";

                    return (
                      <td
                        className={`timeline-cell timeline-cell--${bgTop} timeline-cell-bottom--${bgBottom}`}
                        key={`${slot.key}-${cell.trainer.id}`}
                      >
                        {cell.booking && cell.showBookingCard ? (() => {
                          const topPercent = Math.max(
                            0,
                            Math.min(100, ((cell.booking.__startMinutes - rowStart) / 60) * 100)
                          );
                          const heightPercent = Math.max(
                            10,
                            ((cell.booking.__endMinutes - cell.booking.__startMinutes) / 60) * 100
                          );
                          const hoverTitle = [
                            `Batch: ${cell.booking.__batchCode} ${cell.booking.__batchName}`,
                            `Course: ${cell.booking.__course}`,
                            `Start: ${cell.booking.start_time || "-"}`,
                            `End: ${cell.booking.end_time || "-"}`,
                            `Status: ${cell.booking.class_status || "-"}`,
                            `Space: ${cell.booking.__spaceName} (Floor ${cell.booking.__floor})`,
                          ].join("\n");

                          return (
                            <div
                              className="booked-card timeline-booked-card"
                              style={{ top: `${topPercent}%`, height: `${heightPercent}%` }}
                              title={hoverTitle}
                            >
                              <strong>{cell.booking.__batchCode}</strong>
                            </div>
                          );
                        })() : !cell.booking ? (
                          <div className="slot-empty slot-empty--timeline" />
                        ) : null}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default TrainerSlotAvailability;

